import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../SearchableSelect';
import SearchableBankSelect from '../SearchableBankSelect';
import { requestTheme } from '../../utils/requestTheme';

// Perusahaan & penerima di-snapshot ke Proses Bank saat bank dipilih, jadi
// keduanya hanya bisa diubah sebelum itu (server menolak juga).
const PARTY_EDITABLE = ['proses_bayar', 'approved'];

export function EditInternalDetailsDialog({ companies = [], departments = [], editDetailsExtra = {}, editDetailsKeterangan, editDetailsLoading, editDetailsTarget, editDetailsTipeRequest, handleSaveEditDetails, requestTypes, setEditDetailsExtra = () => {}, setEditDetailsKeterangan, setEditDetailsTipeRequest, setShowEditDetails, showEditDetails }) {
  const isOps = editDetailsTarget?.category === 'operasional';
  const theme = requestTheme(editDetailsTarget?.category);
  const partyEditable = PARTY_EDITABLE.includes(editDetailsTarget?.status);
  const setExtra = (field, value) => setEditDetailsExtra(prev => ({ ...prev, [field]: value }));
  const missingCompany = isOps && !editDetailsExtra.company_id;
  // Pilihan mengikuti kategori request (IPR/OPR). Nilai yang sudah tersimpan
  // tetap ditampilkan walau kategorinya kemudian dipindah, supaya dialog tidak
  // menampilkan kosong; menggantinya harus ke pilihan kategori yang benar.
  const masterCat = isOps ? 'opr' : 'ipr';
  const typeList = Array.isArray(requestTypes) ? requestTypes : (requestTypes?.[masterCat] || []);
  const currentType = editDetailsTarget?.tipe_request;
  const typeOptions = currentType && !typeList.includes(currentType) ? [currentType, ...typeList] : typeList;
  const companyOptions = (() => {
    const list = companies.filter((c) => (c.category || 'ipr') === masterCat);
    const cur = companies.find((c) => c.id === editDetailsTarget?.company_id);
    return cur && !list.some((c) => c.id === cur.id) ? [cur, ...list] : list;
  })();
  const deptOptions = (() => {
    const cur = editDetailsTarget?.departemen_id;
    return cur && !departments.some((d) => d.id === cur)
      ? [{ id: cur, nama: `${editDetailsTarget.departemen_nama || '-'} (nonaktif)` }, ...departments] : departments;
  })();

  return (
    <Dialog open={showEditDetails} onOpenChange={setShowEditDetails}>
      <DialogContent className={isOps ? 'max-w-xl max-h-[90vh] overflow-y-auto' : 'max-w-md max-h-[90vh] overflow-y-auto'}>
        <DialogHeader>
          <DialogTitle>Edit Detail Permohonan {theme.label}</DialogTitle>
        </DialogHeader>
        {editDetailsTarget && (
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p className="font-semibold font-mono">
                <span className={`mr-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${theme.badge}`}>{theme.code}</span>
                {editDetailsTarget.request_number}
              </p>
              <p className="text-slate-500">{editDetailsTarget.penerima_nama} - Rp {(editDetailsTarget.amount || 0).toLocaleString('id-ID')}</p>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">Tipe Request</Label>
              <select
                className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                value={editDetailsTipeRequest}
                onChange={(e) => setEditDetailsTipeRequest(e.target.value)}
                data-testid="edit-details-tipe-request"
              >
                <option value="">-- Pilih Tipe --</option>
                {typeOptions.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">
                Perusahaan {isOps && <span className="text-red-500">*</span>}
              </Label>
              {partyEditable ? (
                <SearchableSelect
                  options={companyOptions}
                  value={editDetailsExtra.company_id || ''}
                  onChange={(val) => setExtra('company_id', val)}
                  displayKey="nama_perusahaan"
                  valueKey="id"
                  placeholder="Ketik nama perusahaan..."
                  testId="edit-details-company"
                />
              ) : (
                <p className="text-sm font-semibold">{editDetailsTarget.company_name || '-'}</p>
              )}
              {missingCompany && (
                <p className="text-xs text-[#800000]">Wajib diisi sebelum request bisa diproses ke bank.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">Departemen</Label>
              <SearchableSelect
                options={deptOptions}
                value={editDetailsExtra.departemen_id || ''}
                onChange={(val) => setExtra('departemen_id', val)}
                displayKey="nama"
                valueKey="id"
                placeholder="Ketik nama departemen..."
                testId="edit-details-departemen"
              />
            </div>

            {isOps && (
              <div className="space-y-3 p-3 rounded-lg border border-[#e8bcbc] bg-[#fbeaea]/50">
                <p className="text-sm font-semibold text-[#800000]">Data Penerima</p>
                {!partyEditable && (
                  <p className="text-xs text-slate-500">Sudah masuk Proses Bank — revert dulu bila penerima perlu dikoreksi.</p>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Nama Penerima</Label>
                  <Input disabled={!partyEditable} value={editDetailsExtra.penerima_nama || ''} onChange={(e) => setExtra('penerima_nama', e.target.value.toUpperCase())} className="bg-white uppercase" data-testid="edit-details-penerima-nama" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">No. Rekening</Label>
                    <Input disabled={!partyEditable} inputMode="numeric" maxLength={30} value={editDetailsExtra.penerima_rekening || ''} onChange={(e) => setExtra('penerima_rekening', e.target.value.replace(/\D/g, ''))} className="bg-white font-mono" data-testid="edit-details-penerima-rekening" />
                  </div>
                  {partyEditable ? (
                    <SearchableBankSelect id="edit-opr-bank" label="Nama Bank" value={editDetailsExtra.penerima_bank || ''} onChange={(val) => setExtra('penerima_bank', val)} />
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-xs">Nama Bank</Label>
                      <Input disabled value={editDetailsExtra.penerima_bank || ''} className="bg-white" />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">No. WA</Label>
                    <Input disabled={!partyEditable} inputMode="tel" maxLength={16} value={editDetailsExtra.penerima_wa || ''} onChange={(e) => setExtra('penerima_wa', e.target.value.replace(/[^\d+]/g, ''))} className="bg-white font-mono" data-testid="edit-details-penerima-wa" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input disabled={!partyEditable} type="email" value={editDetailsExtra.penerima_email || ''} onChange={(e) => setExtra('penerima_email', e.target.value)} className="bg-white" data-testid="edit-details-penerima-email" />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="font-semibold">Keterangan</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                value={editDetailsKeterangan}
                onChange={(e) => setEditDetailsKeterangan(e.target.value)}
                placeholder="Masukkan keterangan..."
                data-testid="edit-details-keterangan"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowEditDetails(false)}>Batal</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleSaveEditDetails}
                disabled={editDetailsLoading || !editDetailsTipeRequest}
                data-testid="save-edit-details"
              >
                {editDetailsLoading ? 'Menyimpan...' : 'Simpan Perubahan'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
